import React, { useEffect, useRef, useState } from 'react';
import { X, Clock, Phone, User, Truck, CheckCircle2, MapPin, Building2, Star } from 'lucide-react';
import commandCenterService from '../../services/commandCenterService';
import { deriveTripStage } from './mockTrackingData';
import { DEFAULT_MANUAL_ALLOCATION_TIMEOUT_SEC, getTimeoutConfig } from '../../services/configService';

const POLL_INTERVAL_MS = 1000;

// Shown after "Assign via App" sends a real request to the driver — the
// booking stays PENDING until the driver actually accepts (or declines/times
// out) via their own app, so this polls the booking's real status instead of
// assuming success the moment the request was sent. Layout (Allocation
// Status summary card, Vehicle/Driver info card, state-specific center
// block) intentionally mirrors the Hospital dashboard's ManualAllocationModal
// for a consistent feel across both dashboards — Hospital field is skipped
// on purpose, Command Center bookings frequently don't have one.
const CommandCenterWaitingForDriverPopup = ({ bookingId, driver, onClose, onConfirmed, onDeclined, onDone }) => {
  const [phase, setPhase] = useState('waiting'); // 'waiting' | 'confirmed' | 'declined'
  const [responseWindow, setResponseWindow] = useState(DEFAULT_MANUAL_ALLOCATION_TIMEOUT_SEC);
  const [remainingSeconds, setRemainingSeconds] = useState(DEFAULT_MANUAL_ALLOCATION_TIMEOUT_SEC);
  const [bookingSnapshot, setBookingSnapshot] = useState(null);
  const stoppedRef = useRef(false);
  const startTimeRef = useRef(null);

  useEffect(() => {
    getTimeoutConfig().then((cfg) => {
      if (cfg?.manual_allocation_timeout_sec) {
        setResponseWindow(cfg.manual_allocation_timeout_sec);
      }
    });
  }, []);

  useEffect(() => {
    stoppedRef.current = false;
    startTimeRef.current = Date.now();
    let pollTimeoutId;
    let tickIntervalId;

    const poll = async () => {
      if (stoppedRef.current) return;
      try {
        const result = await commandCenterService.getBooking(bookingId);
        const booking = result.data;
        if (stoppedRef.current) return;
        setBookingSnapshot(booking);

        if (booking.status === 'CONFIRMED') {
          setPhase('confirmed');
          onConfirmed?.(booking);
          return;
        }
        if (booking.status === 'PENDING' && !booking.driver_id) {
          // Cleared by findAndAssignNextDriver's manual-allocation branch —
          // the driver declined or the 30-second response window expired.
          setPhase('declined');
          onDeclined?.(booking);
          return;
        }
      } catch {
        // Transient fetch error — just try again on the next tick.
      }
      pollTimeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    };

    tickIntervalId = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setRemainingSeconds(Math.max(0, responseWindow - elapsed));
    }, 1000);
    poll();

    return () => {
      stoppedRef.current = true;
      clearTimeout(pollTimeoutId);
      clearInterval(tickIntervalId);
    };
    // eslint-disable-next-line
  }, [bookingId]);

  // Closing while a request is still live must actually cancel it server-side
  // (same as Hospital's stopAllocation) — otherwise the real 30s request kept
  // running in the background and could confirm after the dispatcher already
  // walked away. Once accepted, there's nothing left to cancel. stoppedRef is
  // set FIRST, synchronously, so a poll tick already in flight can't react to
  // the driver_id the cancel call is about to clear.
  const handleCancelClick = async () => {
    const wasWaiting = phase === 'waiting';
    const wasConfirmed = phase === 'confirmed';
    stoppedRef.current = true;
    if (wasWaiting) {
      try {
        await commandCenterService.cancelPendingRequest(bookingId);
      } catch {
        // Best-effort — e.g. the driver responded in the same instant. Still
        // close locally either way; the poller/backend state is authoritative.
      }
    }
    onClose();
    // "Done" (confirmed phase) also takes the dispatcher to the Trips page,
    // opened on whichever tab this booking actually landed in.
    if (wasConfirmed) onDone?.(deriveTripStage(bookingSnapshot));
  };

  const progressPercent = Math.max(0, Math.min(100, (remainingSeconds / responseWindow) * 100));
  const fare = bookingSnapshot?.total_price ?? bookingSnapshot?.estimated_fare ?? bookingSnapshot?.platform_service_fee ?? bookingSnapshot?.agreed_price ?? bookingSnapshot?.system_price;
  const confirmedDriver = bookingSnapshot?.driver;
  const confirmedVehicle = bookingSnapshot?.vehicle;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={handleCancelClick}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Manual Allocation</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {phase === 'confirmed'
                ? 'Booking confirmed successfully!'
                : phase === 'declined'
                ? 'Driver did not respond in time'
                : 'Waiting for driver response…'}
            </p>
          </div>
          <button onClick={handleCancelClick} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {(phase === 'waiting' || phase === 'declined') && (
            <>
              <div className={`border rounded-lg p-4 ${phase === 'declined' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Allocation Status</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Booking ID:</span>
                    <span className="font-medium">{bookingId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Allocation Type:</span>
                    <span className="font-medium">MANUAL</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`font-medium ${phase === 'declined' ? 'text-red-600' : 'text-blue-600'}`}>
                      {phase === 'declined' ? 'TIMEOUT_PICK_ANOTHER' : 'WAITING'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Driver Response:</span>
                    <span className={`font-medium ${phase === 'declined' ? 'text-red-600' : 'text-gray-600'}`}>
                      {phase === 'declined' ? 'DECLINED / TIMEOUT' : 'PENDING'}
                    </span>
                  </div>
                  {phase === 'waiting' && remainingSeconds > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Expires in:</span>
                      <span className="font-mono font-bold text-lg text-orange-600 bg-orange-50 px-3 py-1 rounded-md border-2 border-orange-300 animate-pulse">
                        {remainingSeconds}s
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Truck className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900">{driver?.vehicleTypeLabel || 'Ambulance'}</h3>
                    <p className="text-sm text-blue-700">
                      Vehicle: {driver?.vehiclePlate || 'N/A'}
                      {fare != null && <> &middot; Estimated Fare: ₹{Math.round(fare)}</>}
                    </p>
                  </div>
                </div>

                <div className="border-t border-blue-200 pt-4">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">{driver?.name}</p>
                      <p className="text-sm text-blue-700">Driver</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">{driver?.phone || 'N/A'}</p>
                      {driver?.rating != null && (
                        <p className="text-sm text-blue-700 flex items-center gap-1">
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                          {driver.rating}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {phase === 'waiting' && (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">Waiting for driver to accept…</p>
              <div className="w-full bg-blue-200 rounded-full h-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-1000"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-5">
                Closing this cancels the request — the driver will be notified to stop waiting.
              </p>
            </div>
          )}

          {phase === 'declined' && (
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-orange-600 mb-4">
                <Clock className="w-8 h-8" />
                <span className="text-xl font-semibold">Driver Timeout</span>
              </div>
              <p className="text-gray-600 mb-6">Select a different ambulance to continue</p>
              <button
                type="button"
                onClick={onClose}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
              >
                Select Different Ambulance
              </button>
            </div>
          )}

          {phase === 'confirmed' && (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-green-900 mb-2">Booking Confirmed!</h3>
              <p className="text-green-700 mb-6">Your ambulance booking has been confirmed with the driver.</p>

              <div className="bg-green-50 rounded-lg p-4 mb-2 text-left">
                <h4 className="font-semibold text-gray-900 mb-3">Booking Details</h4>

                <div className="mb-3 pb-3 border-b border-green-200">
                  <p className="text-sm text-gray-600">Booking ID:</p>
                  <p className="font-medium text-gray-900 font-mono">{bookingId}</p>
                </div>

                {(bookingSnapshot?.pickup_address || bookingSnapshot?.dropoff_address) && (
                  <div className="mb-3 pb-3 border-b border-green-200 space-y-1.5">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-800">{bookingSnapshot?.pickup_address || 'Pickup location'}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Building2 className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-800">{bookingSnapshot?.dropoff_address || 'Drop-off location'}</span>
                    </div>
                  </div>
                )}

                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Truck className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {confirmedVehicle?.type?.name || driver?.vehicleTypeLabel || 'Ambulance'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {confirmedVehicle?.plate_number || driver?.vehiclePlate || 'N/A'}
                        {fare != null && <> &bull; ₹{Math.round(fare)}</>}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <User className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{confirmedDriver?.name || driver?.name}</p>
                      <p className="text-sm text-gray-600">{confirmedDriver?.phone || driver?.phone || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 p-3 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800">Driver Confirmed</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {phase !== 'declined' && (
          <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleCancelClick}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                phase === 'confirmed'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {phase === 'confirmed' ? 'Done' : 'Cancel'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommandCenterWaitingForDriverPopup;
